/**
 * Auth-cache listener.
 *
 * Clears identity-scoped client-side state whenever the auth identity
 * changes - i.e. on `loginSucceeded` and `logout`. Two things are
 * cleared: the RTK Query cache, and the copilot drawer's persisted
 * conversation id in localStorage.
 *
 * Why this exists (audit finding V6; extended by the tenant-isolation
 * audit V2):
 * RTK Query caches endpoint responses in the store. Without this
 * listener, the cache survives a login / logout / org-switch that
 * happens WITHOUT a full page reload. Concretely: User A logs in and
 * loads their people list; User B then logs in on the same tab; until
 * each cache entry expires, B's app can render A's cached tenant data.
 *
 * `resetApiState()` clears every cached query and subscription, so the
 * first request after an identity change always goes to the network
 * with the new tenant's headers. It is dispatched on BOTH actions:
 *   - loginSucceeded: covers a different user / org logging in
 *   - logout:         covers leaving a session on a shared browser
 *
 * Implemented as a listener (not inline at the dispatch sites) so it
 * holds no matter where those actions are dispatched from - including
 * future logout UI that doesn't exist yet.
 */
import { createListenerMiddleware } from "@reduxjs/toolkit";

import { baseApi } from "@/app/store/api/baseApi";

import { loginSucceeded, logout } from "@/features/auth/authSlice";
import { COPILOT_ACTIVE_CONVERSATION_KEY } from "@/features/copilot/ui/CopilotDrawer";

/** Redux listener that clears identity-scoped client state when the auth identity changes. */
export const authCacheListener = createListenerMiddleware();

authCacheListener.startListening({
  matcher: (action) =>
    loginSucceeded.match(action) || logout.match(action),
  effect: (_action, listenerApi) => {
    // Drop all cached query results + subscriptions. The next request
    // re-fetches with the current identity's tenant + token headers.
    listenerApi.dispatch(baseApi.util.resetApiState());

    // RTK Query is not the only client-side state keyed to an identity:
    // the copilot drawer persists its last-open conversation id in
    // localStorage. Clear it on any identity change so a new user on a
    // shared browser does not inherit a previous user's conversation id
    // (tenant-isolation audit V2). Backend scoping already blocks the
    // actual data - this prevents the drawer opening to a stale id.
    try {
      window.localStorage.removeItem(COPILOT_ACTIVE_CONVERSATION_KEY);
    } catch {
      // localStorage may be unavailable (private browsing); non-fatal.
    }
  },
});
